import composeRefs from '@jetbrains/ring-ui/components/global/composeRefs'
import {H4} from '@jetbrains/ring-ui/components/heading/heading'
import type {Ref} from 'react'
import {forwardRef} from 'react'
import * as React from 'react'

import useEventCallback from '../../../hooks/useEventCallback'
import type {GetAvailableRunnersResponse} from '../../../services/rest'
import SearchInput from '../../common/SearchInput/SearchInput'

import SelectBuildRunnersGroup from './SelectBuildRunners.group'
import type {RunnerType} from './SelectBuildRunners.types'

import styles from './SelectBuildRunners.css'

interface Props {
  runners: GetAvailableRunnersResponse
  onSelect: () => void
  onLoadingComplete: () => void
}

function SelectBuildRunnersListing(
  {runners, onSelect, onLoadingComplete}: Props,
  inputRef: Ref<HTMLInputElement>,
) {
  const [searchString, setSearchString] = React.useState('')
  const [focusedIndex, setFocusedIndex] = React.useState(0)
  const shouldShowGroups = Object.keys(runners).length > 1
  const ref = React.useRef<HTMLInputElement>()

  const updateSearch = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFocusedIndex(0)
    setSearchString(event.target.value)
  })

  React.useEffect(() => {
    ref.current?.focus()
  }, [])

  const filteredGroups: [string, RunnerType[]][] = React.useMemo(() => {
    const search = searchString.toLowerCase()

    return Object.entries(runners).map(([group, runnerItems]) => [
      group,
      runnerItems.filter(runner => {
        const name = runner.name?.toLowerCase() ?? ''
        const description = runner.description?.toLowerCase() ?? ''
        return name.includes(search) || description.includes(search)
      }),
    ])
  }, [runners, searchString])

  const flattenRunners = React.useMemo(
    () => filteredGroups.flatMap(([, runnerItems]) => runnerItems),
    [filteredGroups],
  )

  const focusedRunner = flattenRunners[focusedIndex]

  const handleSubmit = useEventCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (focusedRunner) {
      window.BS?.selectRunnerInFlattenMode?.(focusedRunner.key, onLoadingComplete)
      onSelect()
    }
  })

  const handleKeyDown = useEventCallback((event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setFocusedIndex(prev => Math.min(prev + 1, flattenRunners.length - 1))
        break
      case 'ArrowUp':
        event.preventDefault()
        setFocusedIndex(prev => Math.max(prev - 1, 0))
        break
      default:
        break
    }
  })

  const content = React.useMemo(
    () =>
      filteredGroups.map(([group, runnerItems]) => (
        <React.Fragment key={group}>
          {shouldShowGroups && <H4>{group}</H4>}
          <SelectBuildRunnersGroup
            focusedRunner={focusedRunner}
            onSelect={onSelect}
            onLoadingComplete={onLoadingComplete}
            items={runnerItems}
          />
        </React.Fragment>
      )),
    [filteredGroups, shouldShowGroups, focusedRunner, onSelect, onLoadingComplete],
  )

  return (
    <form onSubmit={handleSubmit}>
      <SearchInput
        placeholder={'Filter runner types'}
        value={searchString}
        onKeyDown={handleKeyDown}
        onChange={updateSearch}
        className={styles.filter}
        autoComplete={'off'}
        inputRef={composeRefs(ref, inputRef)}
      />
      <div className={styles.container}>{content}</div>
    </form>
  )
}

export default React.memo(forwardRef(SelectBuildRunnersListing))
